import { useSelector } from 'react-redux';
import { useTheme, Box, Typography, Paper, IconButton, Tooltip } from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { capitalizeFirstLetter } from 'state/helpers';
import { hasPermission } from 'state/checkPermission';
import { LAYOUT_HEIGHT } from 'state/constants';
import FlexCenter from 'components/FlexCenter';


const SKIP_KEYS = new Set(['_id', '__v', 'deletedAt', 'deletedBy', 'createdAt', 'updatedAt', 'updatedBy', 'crewCode', 'crewName', 'startDate', 'endDate']);

const formatVal = (v) => {
    if (v == null) return '—';
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    if (typeof v === 'number') return v.toLocaleString('vi-VN');
    if (typeof v === 'string') {
        if (/^\d{4}-\d{2}-\d{2}/.test(v)) {
            try { return new Date(v).toLocaleDateString(); } catch { }
        }
        return v;
    }
    if (Array.isArray(v)) {
        if (v.length === 0) return '—';
        if (v.some(item => item !== null && typeof item === 'object')) return `${v.length} items`;
        return v.join(', ');
    }
    if (typeof v === 'object') return '—';
    return String(v);
};

const KPIDataCard = ({ row, index, canDelete, onRequestDelete }) => {
    const theme = useTheme();

    const crewKpiCount = Array.isArray(row.crewKpi) ? row.crewKpi.length : null;
    const scalarEntries = Object.entries(row).filter(([k, v]) =>
        !SKIP_KEYS.has(k) && k !== 'crewKpi' && !Array.isArray(v) && (typeof v !== 'object' || v === null)
    );

    return (
        <Paper 
            elevation={1} 
            sx={{ 
                p: 1.5, 
                borderLeft: `2px solid ${theme.palette.primary.main}`, 
                borderRadius: 1 
            }}
        >
            <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    #{index + 1}
                </Typography>
                <Box flex={1} minWidth={0}>
                    <Typography variant="subtitle2" fontWeight="bold">
                        {row.startDate ? formatVal(row.startDate) : '—'}
                        {' → '}
                        {row.endDate ? formatVal(row.endDate) : '—'}
                    </Typography>
                </Box>
                {canDelete && (
                    <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => onRequestDelete(row._id)}
                            sx={{ color: theme.palette.primary.dark, '&:hover': { color: theme.palette.green?.deep } }}>
                            <DeleteForeverIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            {crewKpiCount !== null && (
                <Typography variant="caption" color="text.secondary" display="block" mt={0.75}>
                    Crew KPIs: <strong>{crewKpiCount}</strong> members
                </Typography>
            )}

            {scalarEntries.length > 0 && (
                <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" gap={0.75} mt={0.75}>
                    {scalarEntries.map(([k, v]) => (
                        <Box key={k}>
                            <Typography variant="caption" color="text.secondary" display="block" noWrap>
                                {capitalizeFirstLetter(k)}
                            </Typography>
                            <Typography variant="body2" fontWeight="medium">{formatVal(v)}</Typography>
                        </Box>
                    ))}
                </Box>
            )}
        </Paper>
    );
};


const CrewKPIDataList = ({ rows, onRequestDelete }) => {
    const user = useSelector((state) => state.user);
    const canDelete = hasPermission(user?.permissions, "CrewKpis", "delete", false);

    if (!rows?.length) {
        return (
            <FlexCenter height={LAYOUT_HEIGHT}>
                <Typography color="error">No data available</Typography>
            </FlexCenter>
        );
    }

    return (
        <Box
            display="flex"
            flexDirection="column"
            width="100%"
            gap={2}
        >
            {rows.map((row, index) => (
                <KPIDataCard
                    key={row._id}
                    row={row}
                    index={index}
                    canDelete={canDelete}
                    onRequestDelete={onRequestDelete}
                />
            ))}
        </Box>
    );
};

export default CrewKPIDataList;
